import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { cn } from "./utils";

/**
 * Single-date picker. Returns / accepts a Date.
 */
export function DatePicker({ value, onChange, min, max, placeholder = "Pick a date", "data-testid": testId, className }) {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "w-full flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-900 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none",
            !value && "text-slate-400",
            className
          )}
        >
          <CalendarIcon size={14} className="text-slate-500" />
          {value ? format(value, "PP") : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(d) => {
            if (d) {
              onChange(d);
              setOpen(false);
            }
          }}
          disabled={(d) => (min && d < startOfDay(min)) || (max && d > endOfDay(max))}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Date-range picker (from/to). Returns / accepts { from, to }.
 */
export function DateRangePicker({ value, onChange, min, placeholder = "Pick dates", "data-testid": testId, className }) {
  const [open, setOpen] = React.useState(false);
  const label = value?.from
    ? value.to
      ? `${format(value.from, "PP")} → ${format(value.to, "PP")}`
      : `${format(value.from, "PP")} → …`
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "w-full flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-900 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none",
            !value?.from && "text-slate-400",
            className
          )}
        >
          <CalendarIcon size={14} className="text-slate-500" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          numberOfMonths={2}
          selected={value}
          onSelect={(r) => {
            onChange(r);
            if (r?.from && r?.to) setOpen(false);
          }}
          disabled={(d) => (min && d < startOfDay(min))}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Lightweight time picker using a styled <select> for 00:00 … 23:30 half-hour slots.
 */
export function TimePicker({ value, onChange, "data-testid": testId, step = 30 }) {
  const options = React.useMemo(() => {
    const out = [];
    for (let m = 0; m < 24 * 60; m += step) {
      const h = String(Math.floor(m / 60)).padStart(2, "0");
      const mm = String(m % 60).padStart(2, "0");
      out.push(`${h}:${mm}`);
    }
    return out;
  }, [step]);
  return (
    <select
      data-testid={testId}
      value={value || "10:00"}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none"
    >
      {options.map((t) => (
        <option key={t} value={t}>{t}</option>
      ))}
    </select>
  );
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function combineDateTime(date, time = "10:00") {
  if (!date) return null;
  const [h, m] = (time || "10:00").split(":").map((n) => parseInt(n, 10));
  const d = new Date(date);
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
}

export function isoFromDateTime(date, time = "10:00") {
  const dt = combineDateTime(date, time);
  return dt ? dt.toISOString() : null;
}
